import React, { Suspense, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import type { GalleryItem } from '../types/gallery';
import Layout from '../components/Layout';
import ProtectedRoute from '../components/ProtectedRoute';
import AdminRoute from '../components/AdminRoute';
import OrdersStaffRoute from '../components/OrdersStaffRoute';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import RegisterCompanyPage from '../pages/RegisterCompanyPage';
import OrganizationPage from '../pages/OrganizationPage';
import HomePage from '../components/HomePage';
import GalleryPage from '../components/GalleryPage';
import Editor from '../components/Editor';
import MoodboardsPage from '../components/MoodboardsPage';
import ProfilePage from '../components/ProfilePage';
import HelpPage from '../components/HelpPage';
import TermsPage from '../components/TermsPage';
import PrivacyPage from '../components/PrivacyPage';
import SupportPage from '../pages/SupportPage';
import UserTickets from '../pages/UserTickets';
import AdminCompaniesPage from '../pages/admin/AdminCompaniesPage';
import AdminCompanyDetailPage from '../pages/admin/AdminCompanyDetailPage';

import CustomerDetailPage from '../components/admin/CustomerDetailPage';
import AcceptInvitePage from '../pages/AcceptInvitePage';
import CheckoutSuccessPage from '../pages/CheckoutSuccessPage';
import CheckoutCancelPage from '../pages/CheckoutCancelPage';
import QuickEditModal from '../components/shared/QuickEditModal';
import AuthModal from '../components/shared/AuthModal';
import TermsAcceptanceBar from '../components/shared/legal/TermsAcceptanceBar';
import FeedbackModal from '../components/shared/FeedbackModal';
import ModuleGate from '../components/shared/ModuleGate';
import MarketingSegmentRoute from '../components/marketing-segment/MarketingSegmentRoute';
import { APP_HOME } from '../utils/segment';

// DEV-ONLY: Design System showcase — tree-shaken from production/staging builds.
// import.meta.env.DEV is a literal `false` in `vite build`, so Vite's dead-code
// elimination drops the dynamic import AND the route path string "/__ds-preview"
// — neither the showcase chunk nor the route literal is emitted in the prod bundle.
const DsPreviewPage = import.meta.env.DEV
  ? React.lazy(() =>
      import('../pages/DsPreviewPage').then((m) => ({ default: m.DsPreviewPage }))
    )
  : null;

// Lazy-loaded heavy pages for better initial bundle size
const LandingPage = React.lazy(() => import('../pages/LandingPage'));
const BatchCreation = React.lazy(() => import('../components/BatchCreation'));
const InstagramIntegrationPage = React.lazy(() => import('../pages/InstagramIntegrationPage'));
const HomeDecor = React.lazy(() => import('../components/HomeDecor'));
const Moodboard = React.lazy(() => import('../components/Moodboard'));
const BrandbookPage = React.lazy(() => import('../components/BrandbookPage'));
const Admin = React.lazy(() => import('../pages/Admin'));
const StillsVestuarioPage = React.lazy(() => import('../pages/StillsVestuarioPage'));
const StillsProdutosPage = React.lazy(() => import('../pages/StillsProdutosPage'));
const EyewearPage = React.lazy(() => import('../pages/EyewearPage'));
const JewelryPage = React.lazy(() => import('../pages/JewelryPage'));
const VeoStudioPage = React.lazy(() => import('../pages/VeoStudioPage'));
const ModelCreatorPage = React.lazy(() => import('../pages/ModelCreatorPage'));
const MyModelsPage = React.lazy(() => import('../pages/MyModelsPage'));
const MarketingStudioPage = React.lazy(() => import('../pages/MarketingStudioPage'));
const AdminMarketingTemplatesPage = React.lazy(() => import('../pages/admin/AdminMarketingTemplatesPage'));
const ConnectorsModePage = React.lazy(() => import('../pages/ConnectorsModePage'));
const FacadesPage = React.lazy(() => import('../pages/FacadesPage'));
const TrendForgeWizardPage = React.lazy(() => import('../pages/trendforge/TrendForgeWizardPage'));
const VTOAttributionPage = React.lazy(() => import('../pages/admin/VTOAttributionPage'));
const VTOSessionsPage = React.lazy(() => import('../pages/admin/VTOSessionsPage'));
const MyOrdersPage = React.lazy(() => import('../pages/MyOrdersPage'));
const OrderDeliveryPage = React.lazy(() => import('../pages/OrderDeliveryPage'));
const AdminClientsPage = React.lazy(() => import('../pages/admin/AdminClientsPage'));
const AdminKanbanPage = React.lazy(() => import('../pages/admin/AdminKanbanPage'));
const AdminOrderPage = React.lazy(() => import('../pages/admin/AdminOrderPage'));
// Segmento Marketing (seletor Fashion | Marketing na barra superior).
const MarketingHomePage = React.lazy(() => import('../pages/marketing/MarketingHomePage'));
const BrandDnaPage = React.lazy(() => import('../pages/marketing/BrandDnaPage'));
const NewBrandPage = React.lazy(() => import('../pages/marketing/NewBrandPage'));
// POC descartável (/poc1): apagar junto com a pasta pages/poc.
const PocMercadoPage = React.lazy(() => import('../pages/poc/PocMercadoPage'));
const ProductsPage = React.lazy(() => import('../pages/marketing/ProductsPage'));
const ImportProductsPage = React.lazy(() => import('../pages/marketing/ImportProductsPage'));
const ImageAdPage = React.lazy(() => import('../pages/marketing/ImageAdPage'));
const ImageAdBuilderPage = React.lazy(() => import('../pages/marketing/ImageAdBuilderPage'));
const BatchVariationsPage = React.lazy(() => import('../pages/marketing/BatchVariationsPage'));
const CompetitorsPage = React.lazy(() => import('../pages/marketing/CompetitorsPage'));
const ImageAdDetailPage = React.lazy(() => import('../pages/marketing/ImageAdDetailPage'));
const AdsLibraryPage = React.lazy(() => import('../pages/marketing/AdsLibraryPage'));
const VideoAdPage = React.lazy(() => import('../pages/marketing/VideoAdPage'));
const VideoAdBuilderPage = React.lazy(() => import('../pages/marketing/VideoAdBuilderPage'));
const ReferencesPage = React.lazy(() => import('../pages/marketing/ReferencesPage'));
const MetaConnectionPage = React.lazy(() => import('../pages/marketing/MetaConnectionPage'));
const MetaCallbackPage = React.lazy(() => import('../pages/integrations/MetaCallbackPage'));
const InstagramCallbackPage = React.lazy(() => import('../pages/integrations/InstagramCallbackPage'));

const LazyFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7063f7]" />
  </div>
);

import { useAuthStore } from '../stores/useAuthStore';
import { isSnapfyInternal } from '../utils/snapfyInternal';
import { useGalleryStore } from '../stores/useGalleryStore';
import { useGenerationStore } from '../stores/useGenerationStore';
import { useFeedbackModalStore } from '../stores/useFeedbackModalStore';
import { useHomeGeneration } from '../hooks/useHomeGeneration';
import { getPendingGeneration, clearPendingGeneration, restorePendingFile } from '../hooks/usePendingGeneration';

const AppRouter: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const {
    items: userGallery,
    brandbooks,
    fetchBrandbooks,
    addItem: handleItemGeneratedForBatch,
    updateItem,
    addBrandbook: handleAddBrandbook,
  } = useGalleryStore();
  const {
    homePageResult,
    isLoading,
    error,
    prompt,
    editingImage,
    editingImageOrderContext,
    setPrompt,
    setEditingImage,
    setHomePageResult,
  } = useGenerationStore();
  const { handleGenerate, handleSelectStyle, handleDownload } = useHomeGeneration();
  const navigate = useNavigate();

  // Video creation is unified on the /video page: instead of opening a modal, send
  // the chosen image to /video preloaded as the start image (image-to-video mode).
  // Resolve a backendId so /video can load the image via the proxy (avoids S3 CORS):
  // some sources only carry `id` ("img_<backendId>"), not a separate backendId field.
  const handleStartVideoCreation = useCallback((item: GalleryItem | null) => {
    if (!item || item.type !== 'image') return;
    const backendId = item.backendId != null
      ? Number(item.backendId)
      : (/^img_\d+$/.test(item.id) ? Number(item.id.replace('img_', '')) : undefined);
    navigate('/video', { state: { startImage: { src: item.src, backendId, type: 'image' } } });
  }, [navigate]);
  const feedbackIsOpen = useFeedbackModalStore((state) => state.isOpen);
  const feedbackCloseModal = useFeedbackModalStore((state) => state.closeModal);

  // Restore pending generation after login
  const pendingGenerationProcessed = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !pendingGenerationProcessed.current) {
      const pending = getPendingGeneration();
      if (pending) {
        pendingGenerationProcessed.current = true;
        clearPendingGeneration();
        // Restore the file and trigger generation
        restorePendingFile(pending).then((file) => {
          if (file || pending.prompt) {
            setPrompt(pending.prompt);
            // Small delay to ensure state is updated
            setTimeout(() => {
              handleGenerate(file ? [file as File] : [], pending.prompt, pending.styleDetails, pending.aspectRatio);
            }, 500);
          }
        });
      }
    }
  }, [isAuthenticated, handleGenerate, setPrompt]);

  // Fetch brandbooks on mount. A galeria NAO e pre-carregada aqui: cada
  // consumidor de items busca sob demanda (GalleryPage faz o proprio fetch
  // paginado/filtrado; GallerySourceNode chama fetchGallery se items vazio).
  // Evita 1 GET /images (full-res, sem thumbnail) em toda sessao do app.
  useEffect(() => {
    fetchBrandbooks();
  }, [fetchBrandbooks]);

  // Handler for batch item updates — maps (id, newSrc) to updateItem
  const handleItemUpdatedForBatch = (id: string, newSrc: string) => {
    updateItem(id, { src: newSrc } as any);
  };

  return (
    <>
      <Suspense fallback={<LazyFallback />}>
      <Routes>
        {/* DEV-ONLY showcase — public, unauthenticated, tree-shaken from prod/staging bundle */}
        {import.meta.env.DEV && DsPreviewPage && (
          <Route path="/__ds-preview" element={<DsPreviewPage />} />
        )}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/company/invite/accept" element={<AcceptInvitePage />} />
        <Route path="/billing/success" element={<CheckoutSuccessPage />} />
        <Route path="/billing/cancel" element={<CheckoutCancelPage />} />
        <Route path="/support" element={<SupportPage />} />
        {/* snapfy.ai: landing pública (Marketing Studio + Product Studio), fora do layout do app. */}
        <Route path="/" element={<LandingPage />} />
        {/* Página pública da integração com o Instagram (exigida pela Meta; fora do menu, noindex). */}
        <Route path="/instagram" element={<InstagramIntegrationPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            {/* Product Studio (antiga home "/"): geração do Fashion. */}
            <Route path={APP_HOME} element={
              <HomePage
                error={error}
                isLoading={isLoading}
                homePageResult={homePageResult}
                prompt={prompt}
                onPromptChange={setPrompt}
                onGenerate={handleGenerate}
                onSelectStyle={handleSelectStyle}
                onDownload={handleDownload}
                onEditImage={setEditingImage}
                onStartVideoCreation={handleStartVideoCreation}
              />
            } />
            <Route path="/batch" element={
              <BatchCreation
                brandbooks={brandbooks}
                userGallery={userGallery}
                userEmail={user?.email}
                onItemGenerated={handleItemGeneratedForBatch}
                onItemUpdated={handleItemUpdatedForBatch}
                onEditItem={setEditingImage}
                onStartVideoCreation={handleStartVideoCreation}
                onRefreshBrandbooks={fetchBrandbooks}
              />
            } />
            <Route path="/homedecor" element={
              <HomeDecor
                brandbooks={brandbooks}
                userGallery={userGallery}
                onItemGenerated={handleItemGeneratedForBatch}
                onEditItem={setEditingImage}
                onStartVideoCreation={handleStartVideoCreation}
              />
            } />
            <Route path="/editor" element={<Editor />} />
            <Route path="/gallery" element={
              <GalleryPage items={userGallery} onEditItem={setEditingImage} onStartVideoCreation={handleStartVideoCreation} />
            } />
            <Route path="/moodboards" element={<MoodboardsPage />} />
            {/* Fachadas: app novo, ainda nao validado com cliente. A rota responde
                apenas para contas @snapfy.ai — esconder so o item do menu deixaria a
                URL acessivel a quem a digitasse. Abrir e trocar por <FacadesPage />. */}
            <Route path="/facades" element={isSnapfyInternal(user) ? <FacadesPage /> : <Navigate to={APP_HOME} replace />} />
            <Route path="/moodboard" element={
              <Moodboard
                onAddBrandbook={handleAddBrandbook}
                onItemGenerated={handleItemGeneratedForBatch}
                onRefreshBrandbooks={fetchBrandbooks}
              />
            } />
            <Route path="/brandbook" element={
              <BrandbookPage brandbooks={brandbooks} onAddBrandbook={handleAddBrandbook} />
            } />
            {/* Operação de Pedidos: equipe @snapfy.ai verificada, não só admin. */}
            <Route element={<OrdersStaffRoute />}>
              <Route path="/admin/clients" element={<AdminClientsPage />} />
              <Route path="/admin/kanban" element={<AdminKanbanPage />} />
              <Route path="/admin/orders/:id" element={<AdminOrderPage />} />
            </Route>

            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/companies" element={<AdminCompaniesPage />} />
              <Route path="/admin/companies/:id" element={<AdminCompanyDetailPage />} />
              <Route path="/admin/vto-attribution" element={<VTOAttributionPage />} />
              <Route path="/admin/vto-sessions" element={<VTOSessionsPage />} />

              <Route path="/admin/marketing-templates" element={<AdminMarketingTemplatesPage />} />

              <Route path="/admin/billing/customer/:userId" element={<CustomerDetailPage />} />
            </Route>
            <Route path="/orders" element={<MyOrdersPage />} />
            <Route path="/orders/:id/delivery" element={<OrderDeliveryPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/tickets" element={<UserTickets />} />
            <Route path="/stills/vestuario" element={<ModuleGate slug="stills_vestuario"><StillsVestuarioPage /></ModuleGate>} />
            <Route path="/stills/produtos" element={<ModuleGate slug="stills_produtos"><StillsProdutosPage /></ModuleGate>} />
            <Route path="/accessories/eyewear" element={<ModuleGate slug="eyewear"><EyewearPage /></ModuleGate>} />
            <Route path="/accessories/jewelry" element={<ModuleGate slug="jewelry"><JewelryPage /></ModuleGate>} />
            <Route path="/video" element={<ModuleGate slug="veo_studio"><VeoStudioPage /></ModuleGate>} />
            {/* Legacy path kept so existing links/bookmarks don't 404. Preserve
                query params (UTM/referral) when redirecting. */}
            <Route path="/veo" element={<Navigate to={{ pathname: '/video', search: window.location.search }} replace />} />
            <Route path="/models" element={<ModuleGate slug="my_models"><MyModelsPage /></ModuleGate>} />
            <Route path="/models/create" element={<ModuleGate slug="model_creator"><ModelCreatorPage /></ModuleGate>} />
            <Route path="/workflow" element={<ModuleGate slug="workflow"><ConnectorsModePage /></ModuleGate>} />
            <Route path="/marketing-studio" element={<ModuleGate slug="marketing_studio"><MarketingStudioPage /></ModuleGate>} />
            <Route path="/trendforge" element={<ModuleGate slug="trendforge"><TrendForgeWizardPage /></ModuleGate>} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/register-company" element={<RegisterCompanyPage />} />
            <Route path="/organization" element={<OrganizationPage />} />
            {/* Segmento Marketing: tudo sob /marketing usa o menu da plataforma Marketing.
                Os apps do Product Studio (Canvas, Workflow, Lookbook, Meus modelos,
                Brandbook, Galeria) também abrem aqui, com as mesmas props e módulos
                das rotas antigas, que continuam valendo. Animar imagem reaproveita o Veo Studio. */}
            <Route path="/poc1" element={<Navigate to="/marketing/poc1" replace />} />
            <Route element={<MarketingSegmentRoute />}>
              <Route path="/marketing" element={<MarketingHomePage />} />
              <Route path="/marketing/canvas" element={
                <Moodboard
                  onAddBrandbook={handleAddBrandbook}
                  onItemGenerated={handleItemGeneratedForBatch}
                  onRefreshBrandbooks={fetchBrandbooks}
                />
              } />
              {/* Playground era o Canvas dentro do Marketing: links antigos caem no Canvas. */}
              <Route path="/marketing/playground" element={<Navigate to={{ pathname: '/marketing/canvas', search: window.location.search }} replace />} />
              <Route path="/marketing/workflow" element={<ModuleGate slug="workflow"><ConnectorsModePage /></ModuleGate>} />
              <Route path="/marketing/lookbook" element={
                <BatchCreation
                  brandbooks={brandbooks}
                  userGallery={userGallery}
                  userEmail={user?.email}
                  onItemGenerated={handleItemGeneratedForBatch}
                  onItemUpdated={handleItemUpdatedForBatch}
                  onEditItem={setEditingImage}
                  onStartVideoCreation={handleStartVideoCreation}
                  onRefreshBrandbooks={fetchBrandbooks}
                />
              } />
              <Route path="/marketing/modelos" element={<ModuleGate slug="my_models"><MyModelsPage /></ModuleGate>} />
              <Route path="/marketing/modelos/criar" element={<ModuleGate slug="model_creator"><ModelCreatorPage /></ModuleGate>} />
              <Route path="/marketing/brandbook" element={
                <BrandbookPage brandbooks={brandbooks} onAddBrandbook={handleAddBrandbook} />
              } />
              <Route path="/marketing/galeria" element={
                <GalleryPage items={userGallery} onEditItem={setEditingImage} onStartVideoCreation={handleStartVideoCreation} />
              } />
              <Route path="/marketing/animar-imagem" element={<ModuleGate slug="veo_studio"><VeoStudioPage /></ModuleGate>} />
              {/* DNA da marca: leitura do site do cliente por IA (app próprio, não é o Brandbook). */}
              <Route path="/marketing/dna" element={<BrandDnaPage />} />
              <Route path="/marketing/dna/novo" element={<NewBrandPage />} />
              {/* POC descartável: cliente novo → mercado → escolha do Jev. */}
              <Route path="/marketing/poc1" element={<PocMercadoPage />} />
              {/* Produtos: catálogo da loja da marca + fila "Revisar". */}
              <Route path="/marketing/produtos" element={<ProductsPage />} />
              <Route path="/marketing/produtos/importar" element={<ImportProductsPage />} />
              {/* Anúncio de imagem: galeria de templates com caixas de edição (Seedream <bbox>). */}
              <Route path="/marketing/anuncio-imagem" element={<ImageAdPage />} />
              <Route path="/marketing/anuncio-imagem/novo" element={<ImageAdBuilderPage />} />
              <Route path="/marketing/variacoes-em-lote" element={<BatchVariationsPage />} />
              <Route path="/marketing/concorrentes" element={<CompetitorsPage />} />
              <Route path="/marketing/anuncio-imagem/criativo/:id" element={<ImageAdDetailPage />} />
              <Route path="/marketing/anuncios" element={<AdsLibraryPage />} />
              <Route path="/marketing/anuncio-video" element={<VideoAdPage />} />
              <Route path="/marketing/anuncio-video/novo" element={<VideoAdBuilderPage />} />
              <Route path="/marketing/referencias" element={<ReferencesPage />} />
              <Route path="/marketing/meta" element={<MetaConnectionPage />} />
              {/* Retorno do Login do Facebook para Empresas (URL cadastrada no app da Meta). */}
              <Route path="/integrations/meta/callback" element={<MetaCallbackPage />} />
              {/* Retorno do Login do Instagram (URL cadastrada no app do Instagram na Meta). */}
              <Route path="/integrations/instagram/callback" element={<InstagramCallbackPage />} />
              <Route path="/marketing/*" element={<Navigate to="/marketing" replace />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={
          <div className="flex flex-col items-center justify-center h-screen bg-[var(--snapfy-bg,#121212)] text-white">
            <h1 className="text-4xl font-bold mb-4">404</h1>
            <p className="text-snapfy-text-secondary mb-6">Pagina nao encontrada</p>
            <a href={APP_HOME} className="px-4 py-2 bg-[#7063f7] hover:bg-[#5a4dd4] rounded-lg transition-colors">
              Voltar ao inicio
            </a>
          </div>
        } />
      </Routes>
      </Suspense>

      <QuickEditModal
        isOpen={!!editingImage && editingImage.type === 'image'}
        onClose={() => setEditingImage(null)}
        baseImage={editingImage}
        orderEdit={editingImageOrderContext ?? undefined}
        onImageEdited={(newImageUrl, newBackendId) => {
          if (editingImage) {
            // Update master gallery
            updateItem(editingImage.id, { src: newImageUrl, backendId: newBackendId || editingImage.backendId } as any);
            // Update home page result if it's the one being edited
            setHomePageResult(
              homePageResult?.id === editingImage.id && homePageResult.type === 'image'
                ? { ...homePageResult, src: newImageUrl, backendId: newBackendId || homePageResult.backendId }
                : homePageResult
            );
          }
        }}
      />
      <AuthModal />
      <TermsAcceptanceBar />
      <FeedbackModal isOpen={feedbackIsOpen} onClose={feedbackCloseModal} />
    </>
  );
};

export default AppRouter;
